import React from "react";
import { Link } from "react-router-dom";
import {
  Flag, ListChecks, MapTrifold, Golf, Target, Trophy, UsersThree,
  Ticket, Crown, ChartBar, Lifebuoy, Lightning, Wind, Mountains, Crosshair,
  CaretRight, ArrowRight, EnvelopeSimple, Scales,
} from "@phosphor-icons/react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import RulesCheatSheet from "@/components/RulesCheatSheet";
import { useAuth } from "@/context/AuthContext";

/**
 * /help — single-page how-to guide for Bula Golf.
 *
 * Designed as a scrollable doc with a sticky "table of contents" sidebar on
 * desktop and an Accordion on mobile. Each section answers a single
 * job-to-be-done ("How do I record a round?"). Keep the writing voice
 * crisp, second-person and Fijian-friendly.
 */

const SECTIONS = [
  { id: "getting-started",   label: "Getting started",        icon: Lightning },
  { id: "record-round",      label: "Record a round",         icon: Golf },
  { id: "live-scorecard",    label: "Live scorecard & GPS",   icon: MapTrifold },
  { id: "rangefinder",       label: "Rangefinder & plays-like", icon: Crosshair },
  { id: "club-bag",          label: "Club bag & smart pick",  icon: Target },
  { id: "stats",             label: "Stats & handicap",       icon: ChartBar },
  { id: "rules",             label: "Rules of golf (simple)", icon: Scales },
  { id: "tournaments",       label: "Tournaments",            icon: Trophy },
  { id: "friends",           label: "Friends & leaderboard",  icon: UsersThree },
  { id: "bookings",          label: "Tee-time bookings",      icon: Ticket },
  { id: "pro",               label: "Bula Pro",               icon: Crown },
  { id: "troubleshoot",      label: "Troubleshooting",        icon: Lifebuoy },
];

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3" data-testid={`help-step-${n}`}>
      <div className="shrink-0 w-7 h-7 rounded-full bg-[#0F2537] text-white flex items-center justify-center text-xs font-black">
        {n}
      </div>
      <div className="flex-1 pt-0.5">
        <div className="font-bold text-[#0F2537]">{title}</div>
        <div className="text-sm text-[#0F2537]/75 mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Tip({ children, kind = "info" }) {
  const palette = kind === "pro"
    ? "bg-[#E05D3A]/10 border-[#E05D3A]/30 text-[#0F2537]"
    : "bg-[#0F2537]/5 border-[#0F2537]/15 text-[#0F2537]";
  const label = kind === "pro" ? "Pro tip" : "Bula tip";
  return (
    <div className={`mt-3 p-3 rounded-xl border text-sm ${palette}`}>
      <span className="label-eyebrow mr-2">{label}</span>
      {children}
    </div>
  );
}

function Section({ id, title, eyebrow, icon: Icon, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`help-section-${id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl bg-[#0F2537] text-[#E05D3A] flex items-center justify-center shrink-0">
          <Icon size={22} weight="fill" />
        </div>
        <div>
          <span className="label-eyebrow">{eyebrow}</span>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mt-0.5">
            {title}
          </h2>
        </div>
      </div>
      <div className="space-y-4 text-[#0F2537]/80">{children}</div>
    </section>
  );
}

function Kbd({ children }) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#0F2537] text-white text-[10px] font-bold tracking-wide">
      {children}
    </span>
  );
}

export default function Help() {
  const { user } = useAuth();

  return (
    <div className="space-y-10" data-testid="help-page">
      {/* Hero */}
      <div className="card-default p-7 md:p-10 rise relative overflow-hidden">
        <div className="relative z-10 max-w-2xl">
          <span className="label-eyebrow">How to use Bula Golf</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-1">
            Your on-course playbook.
          </h1>
          <p className="text-[#0F2537]/75 mt-3 text-base sm:text-lg leading-relaxed">
            Everything you need — recording rounds, the GPS rangefinder,
            tournaments, handicap math, and getting the most out of Bula Pro.
            <i> Vinaka</i> for playing with us. <Flag weight="fill" size={16} className="inline text-[#E05D3A] align-text-bottom" />
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            <a href="#record-round" data-testid="help-quick-record" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-sm font-bold hover:bg-[#0F2537]/90 transition">
              <Golf size={14} weight="fill" /> Record a round
            </a>
            <a href="#rangefinder" data-testid="help-quick-rangefinder" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] text-sm font-bold hover:border-[#0F2537]/40 transition">
              <Crosshair size={14} weight="fill" /> Use the rangefinder
            </a>
            <a href="#rules" data-testid="help-quick-rules" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] text-sm font-bold hover:border-[#0F2537]/40 transition">
              <Scales size={14} weight="fill" /> Rules cheat-sheet
            </a>
            <a href="#pro" data-testid="help-quick-pro" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold hover:brightness-110 transition">
              <Crown size={14} weight="fill" /> What's in Pro
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* TOC — desktop sticky */}
        <aside className="hidden lg:block">
          <nav className="sticky top-24 space-y-1" data-testid="help-toc">
            <span className="label-eyebrow block mb-2">Contents</span>
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <a key={id} href={`#${id}`} data-testid={`help-toc-${id}`}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm font-semibold text-[#0F2537]/75 hover:bg-[#0F2537]/5 hover:text-[#0F2537] transition">
                <Icon size={14} weight="bold" />
                {label}
              </a>
            ))}
            <div className="mt-4 pt-4 border-t border-[#E2E0D8]">
              <Link to="/contact" data-testid="help-contact-link" className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm font-semibold text-[#E05D3A] hover:bg-[#E05D3A]/10 transition">
                <EnvelopeSimple size={14} weight="bold" />
                Still stuck? Contact us
              </Link>
            </div>
          </nav>
        </aside>

        {/* Sections */}
        <div className="space-y-14 min-w-0">
          <Section id="getting-started" eyebrow="Step 1" title="Getting started" icon={Lightning}>
            <p className="text-base leading-relaxed">
              Bula Golf works the moment you sign up — no card needed. Five minutes to
              your first scorecard.
            </p>
            <div className="space-y-4 mt-2">
              <Step n={1} title="Create your account">
                Tap <Kbd>Sign up</Kbd>, enter your email and a password. Add your name and
                home club. If you have a <b>GolfLink</b> number you can add it later in
                Profile — it's purely informational, your live handicap comes from rounds
                you log here.
              </Step>
              <Step n={2} title="Tell us your starting handicap">
                In <Link to="/profile" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Profile → Your card</Link>, set a <b>declared handicap</b>.
                As soon as you post 3 rounds we switch to your <b>World Handicap System
                index</b> calculated from your best differentials.
              </Step>
              <Step n={3} title="Browse the course list">
                Head to <Link to="/courses" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Courses</Link> — we've seeded every
                playable course in Fiji with tees, slope, rating and a satellite
                rangefinder. Tap a course to see hole-by-hole detail or to start a round.
              </Step>
            </div>
            <Tip>You can play any course in the database, not just your home club. The handicap math adjusts to slope &amp; rating automatically.</Tip>
          </Section>

          <Section id="record-round" eyebrow="Step 2" title="Record a round" icon={Golf}>
            <p className="text-base leading-relaxed">
              The fastest path to a scorecard. You can start a round from the
              <Kbd>New Round</Kbd> button in the header or from any course page.
            </p>
            <div className="space-y-4 mt-2">
              <Step n={1} title="Pick your course and tees">
                On <Link to="/rounds/new" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">New Round</Link>, choose the course, then
                tap the tee marker (white / yellow / blue / black) you'll play from. The
                rating and slope shown drive the handicap differential for the round.
              </Step>
              <Step n={2} title="Choose the format">
                Stroke play is the default. Switch to <b>Match play</b>, <b>Best Ball</b>,
                <b> Scramble</b> or <b>4-Ball Better Ball Stableford</b> if you're playing
                with mates. The scorecard view adapts to show partner / opponent scores
                where relevant.
              </Step>
              <Step n={3} title="Start scoring">
                Tap <Kbd>Start round</Kbd>. You'll drop straight into the <b>2-pane swiper
                scorecard</b> on hole 1 — see the next section for how that works.
              </Step>
              <Step n={4} title="Save or quit anytime">
                Rounds auto-save after every hole. Close the tab, come back tomorrow —
                you'll resume on the next un-scored hole. Tap <Kbd>Finish round</Kbd> on
                hole 18 to post the score and refresh your handicap.
              </Step>
            </div>
            <Tip>You can also score a friend's card via <Link to="/friends" className="text-[#E05D3A] font-bold">Friends → Attest</Link>. Both of you get credit when they confirm.</Tip>
          </Section>

          <Section id="live-scorecard" eyebrow="In play" title="Live scorecard & GPS" icon={MapTrifold}>
            <p className="text-base leading-relaxed">
              The live screen is built as a <b>two-pane swiper</b> per hole — one finger
              swipe flips between the GPS rangefinder and the score panel. Hole numbers
              auto-advance once you mark a hole complete.
            </p>
            <div className="space-y-3 mt-2">
              <div className="card-default p-4 flex gap-3">
                <CaretRight weight="bold" size={18} className="text-[#E05D3A] mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold">Left pane — Rangefinder</div>
                  <div className="text-sm text-[#0F2537]/75 mt-0.5">
                    Live satellite map. Drag the orange crosshair to your target, or tap
                    anywhere on the green. Yardage updates in real time. Hit
                    <Kbd>GPS</Kbd> to use your phone's location instead of the tee.
                  </div>
                </div>
              </div>
              <div className="card-default p-4 flex gap-3">
                <CaretRight weight="bold" size={18} className="text-[#E05D3A] mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold">Right pane — Scorecard</div>
                  <div className="text-sm text-[#0F2537]/75 mt-0.5">
                    Tap your gross, log putts, flag fairway hit and GIR. Toggling GIR
                    locks the hole and <b>auto-advances</b> to the next hole's rangefinder
                    after a short delay.
                  </div>
                </div>
              </div>
            </div>
            <Tip>Holding the screen sideways? The swipe still works — pinch-zoom on the map is disabled to avoid double-fires, use the +/− controls.</Tip>
          </Section>

          <Section id="rangefinder" eyebrow="Distance dialled" title="Rangefinder & plays-like" icon={Crosshair}>
            <p className="text-base leading-relaxed">
              The rangefinder doesn't just measure flat distance — it gives you the
              number the ball actually <b>plays like</b> after Fiji's reality lands on it.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <div className="card-default p-4 text-center">
                <Mountains weight="fill" size={22} className="text-[#1E5631] mx-auto" />
                <div className="font-bold mt-1.5">Elevation</div>
                <div className="text-xs text-[#0F2537]/70 mt-0.5">+1 yd per foot of rise, −1 yd per foot of drop. Sourced from Open-Meteo.</div>
              </div>
              <div className="card-default p-4 text-center">
                <Wind weight="fill" size={22} className="text-[#0F2537] mx-auto" />
                <div className="font-bold mt-1.5">Wind</div>
                <div className="text-xs text-[#0F2537]/70 mt-0.5">+1.5 % per mph headwind, −1 % per mph tailwind. Bearing-aware.</div>
              </div>
              <div className="card-default p-4 text-center">
                <Lightning weight="fill" size={22} className="text-[#E05D3A] mx-auto" />
                <div className="font-bold mt-1.5">Air density</div>
                <div className="text-xs text-[#0F2537]/70 mt-0.5">Hot tropical air = thinner air = ball flies further. Adjusted from live temperature.</div>
              </div>
            </div>
            <Tip>
              The big <b>yards-to-pin</b> number is always the plays-like figure. The
              smaller <i>flat XXX yd</i> line below it shows the raw GPS distance, so you
              can sanity-check the correction.
            </Tip>
          </Section>

          <Section id="club-bag" eyebrow="Pro" title="Club bag & smart pick" icon={Target}>
            <p className="text-base leading-relaxed">
              When you log shots, Bula Golf learns your real distances per club. Once you
              have ≥ 5 shots with a given club we compute your <b>average</b> and the
              <b> standard deviation</b> — and surface a club recommendation on every
              rangefinder readout.
            </p>
            <div className="space-y-4 mt-2">
              <Step n={1} title="Build your bag">
                Go to <Link to="/bag" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Profile → My bag</Link> and tick the clubs
                you carry. Optionally pre-fill your stock yardages — we'll calibrate
                these from real shots over time.
              </Step>
              <Step n={2} title="Track shots on course">
                On the rangefinder pane, drag the crosshair to where the ball ended up,
                tap <Kbd>Track</Kbd>, then pick the club you hit. The shot is saved with
                the exact GPS yardage.
              </Step>
              <Step n={3} title="Get the smart-club chip">
                Once you have data, the rangefinder readout shows a small orange chip:
                <span className="block mt-2 mx-auto max-w-xs p-2 rounded-lg bg-[#E05D3A] text-white text-xs font-bold text-center">
                  Hit your <b>7 Iron</b><br />158 ± 6 yd · 92 % to reach
                </span>
                <span className="block mt-2">
                  The <b>92 %</b> is the probability that a typical shot will at least
                  reach the front of the green, computed from your stdev.
                </span>
              </Step>
              <Step n={4} title="Audit consistency">
                <Link to="/stats/clubs" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Stats → Club bag</Link> ranks every club by
                <b> stdev</b> and shows your <b>consistency percentile</b> vs other Bula
                Pro users — useful for picking which club to practise this week.
              </Step>
            </div>
            <Tip kind="pro">The smart-club chip is Bula Pro only — it's powered by stats only Pro users can collect. <Link to="/pricing" className="font-bold underline">Upgrade</Link>.</Tip>
          </Section>

          <Section id="stats" eyebrow="The numbers" title="Stats & handicap" icon={ChartBar}>
            <p className="text-base leading-relaxed">
              We calculate your handicap using the <b>World Handicap System</b>: the
              average of your best 8 differentials from your last 20 rounds (or
              equivalent if fewer rounds posted). The dashboard shows your index, the
              trend, and the rounds counted.
            </p>
            <div className="space-y-3 mt-3">
              <div className="card-default p-4">
                <div className="font-bold flex items-center gap-2"><ListChecks weight="fill" size={16} className="text-[#0F2537]" /> Round-by-round detail</div>
                <p className="text-sm text-[#0F2537]/70 mt-1">Open any round from <Link to="/rounds" className="text-[#E05D3A] font-bold">Rounds</Link> for the full scorecard, putts, fairway %, GIR, double-bogey-plus count and exception status (PCC, ESC caps).</p>
              </div>
              <div className="card-default p-4">
                <div className="font-bold flex items-center gap-2"><ChartBar weight="fill" size={16} className="text-[#0F2537]" /> Strokes Gained (Pro)</div>
                <p className="text-sm text-[#0F2537]/70 mt-1">If you log shot positions, <Link to="/stats/advanced" className="text-[#E05D3A] font-bold">Advanced stats</Link> attributes shots gained / lost vs scratch baseline in OTT (off-the-tee), APP (approach) and ARG (around-the-green).</p>
              </div>
            </div>
          </Section>

          <Section id="rules" eyebrow="Pocket reference" title="Rules of golf (simplified)" icon={Scales}>
            <p className="text-base leading-relaxed">
              A scannable on-course cheat-sheet for the everyday situations. This isn't
              a replacement for the <a href="https://www.randa.org/en/rog/the-rules-of-golf" target="_blank" rel="noreferrer" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">official R&amp;A Rules of Golf</a> —
              just the 90 % you'll actually hit during a round at Natadola or Pearl.
            </p>
            <RulesCheatSheet idPrefix="rule" />
            <Tip>This guide is intentionally brief. For competition-specific or unusual situations, check the <a href="https://www.randa.org/en/rog/the-rules-of-golf" target="_blank" rel="noreferrer" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">R&amp;A Rules of Golf</a> — they've also got a free app that's authoritative for tournament play.</Tip>
          </Section>


          <Section id="tournaments" eyebrow="Compete" title="Tournaments" icon={Trophy}>
            <p className="text-base leading-relaxed">
              Run a one-off club event or a recurring monthly comp. We support stroke
              play, Stableford, match play, and <b>4-Ball Better Ball Stableford</b>
              (best score from each pair counts per hole).
            </p>
            <div className="space-y-4 mt-2">
              <Step n={1} title="Create or join">
                <Link to="/tournaments" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Events</Link> lists open tournaments. Tap
                <Kbd>Create</Kbd> to start your own — choose course, dates, format,
                handicap allowance and entry fee (Pro feature for paid events).
              </Step>
              <Step n={2} title="Set up teams (4-Ball formats)">
                For Better Ball / Scramble, the tournament page has a <b>Teams</b> panel.
                Add two players per team — the leaderboard will auto-pick each pair's
                best Stableford per hole.
              </Step>
              <Step n={3} title="Play the round normally">
                On round day, start the round from your Events page or the tournament
                detail screen. Scores post to the leaderboard in real time.
              </Step>
            </div>
          </Section>

          <Section id="friends" eyebrow="Social" title="Friends & leaderboard" icon={UsersThree}>
            <p className="text-base leading-relaxed">
              See where you stand against your regular four-ball. Friends compare on
              handicap, recent form, total rounds, and best-round trophies.
            </p>
            <div className="space-y-4 mt-2">
              <Step n={1} title="Add friends">
                On <Link to="/friends" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Friends</Link>, search by name or email
                and send a request. They get a push notification (if subscribed) and an
                email.
              </Step>
              <Step n={2} title="Attest scorecards">
                For a round to be counted toward an official handicap event you can
                request an attestation — the attester gets a tap-to-confirm card after
                you finish.
              </Step>
              <Step n={3} title="Climb the leaderboard">
                <Link to="/leaderboard" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Leaderboard</Link> ranks all Bula Golf
                players by lowest current handicap and most rounds in the last 90 days.
                Toggle to <b>Friends only</b> to limit the view.
              </Step>
            </div>
          </Section>

          <Section id="bookings" eyebrow="Off course" title="Tee-time bookings" icon={Ticket}>
            <p className="text-base leading-relaxed">
              Book tee times at participating Fijian courses, or full golf-and-stay
              packages at partnered resorts, without leaving the app.
            </p>
            <div className="space-y-4 mt-2">
              <Step n={1} title="Pick a course or resort">
                <Link to="/booking" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">Book</Link> lists available tee times sorted
                by day. Resort pages show stay + play packages with photos and
                inclusions.
              </Step>
              <Step n={2} title="Confirm and pay">
                Tee times are reserved instantly; resort bookings are confirmed by the
                property within 1 business day. Payment uses Stripe — your card data
                never touches our servers.
              </Step>
              <Step n={3} title="Manage your bookings">
                <Link to="/bookings" className="text-[#E05D3A] font-bold underline-offset-2 hover:underline">My bookings</Link> shows upcoming and past
                reservations with cancellation policies per property.
              </Step>
            </div>
          </Section>

          <Section id="pro" eyebrow="Upgrade" title="Bula Pro" icon={Crown}>
            <p className="text-base leading-relaxed">
              Pro unlocks everything that runs on shot-level data: smart club pick, club
              bag stats, strokes-gained analytics, ad-free shareables and premium
              tournaments. <b>FJ$19/month</b> or <b>FJ$169/year</b> with a 7-day
              money-back guarantee.
            </p>
            <ul className="mt-2 space-y-2 text-sm">
              <li className="flex gap-2"><CaretRight weight="bold" size={14} className="text-[#E05D3A] mt-1 shrink-0" /><span><b>Shot tracking + GPS</b> — every shot saved with club &amp; yardage.</span></li>
              <li className="flex gap-2"><CaretRight weight="bold" size={14} className="text-[#E05D3A] mt-1 shrink-0" /><span><b>Club bag stats</b> — average, stdev, consistency percentile, gap-chart.</span></li>
              <li className="flex gap-2"><CaretRight weight="bold" size={14} className="text-[#E05D3A] mt-1 shrink-0" /><span><b>Smart club pick</b> — plays-like-aware suggestion + probability to reach.</span></li>
              <li className="flex gap-2"><CaretRight weight="bold" size={14} className="text-[#E05D3A] mt-1 shrink-0" /><span><b>Strokes Gained OTT / APP / ARG</b> — pinpoint where you're losing strokes.</span></li>
              <li className="flex gap-2"><CaretRight weight="bold" size={14} className="text-[#E05D3A] mt-1 shrink-0" /><span><b>Premium tournaments</b> — paid entry, sponsor branding, prize tables.</span></li>
              <li className="flex gap-2"><CaretRight weight="bold" size={14} className="text-[#E05D3A] mt-1 shrink-0" /><span><b>Ad-free shareables</b> — branded round-share cards for Instagram &amp; WhatsApp.</span></li>
            </ul>
            <div className="mt-5">
              <Link to="/pricing" data-testid="help-go-pro"
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold ${user?.is_pro ? "border border-[#E2E0D8] text-[#0F2537]" : "bg-[#E05D3A] text-white"}`}>
                {user?.is_pro ? "Manage your Pro subscription" : "Go Pro now"} <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </Section>

          <Section id="troubleshoot" eyebrow="Stuck?" title="Troubleshooting" icon={Lifebuoy}>
            <Accordion type="single" collapsible className="card-default px-4">
              <AccordionItem value="gps">
                <AccordionTrigger data-testid="help-faq-gps">My GPS won't lock on the rangefinder</AccordionTrigger>
                <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed">
                  Make sure your browser has location permission for <code>bulagolf.app</code>.
                  On iOS Safari, go to Settings → Safari → Location → Ask. If you're
                  indoors the first lock can take up to 30 seconds — the map seeds with
                  the hole's tee box in the meantime so you can still measure to the pin.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="handicap">
                <AccordionTrigger data-testid="help-faq-handicap">Why hasn't my handicap moved after a round?</AccordionTrigger>
                <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed">
                  A handicap index updates only if the new round is one of your best 8 of
                  your last 20 differentials. If it isn't your best 8, the index stays
                  put — that's working as intended under the World Handicap System.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="club-chip">
                <AccordionTrigger data-testid="help-faq-club-chip">The smart-club chip doesn't show me a probability</AccordionTrigger>
                <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed">
                  The probability needs at least 5 logged shots with the recommended
                  club so we can compute a meaningful standard deviation. Until then we
                  show the average distance only. Track shots on the rangefinder and it
                  will appear within a round or two.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="email">
                <AccordionTrigger data-testid="help-faq-email">I'm not getting the weekly digest email</AccordionTrigger>
                <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed">
                  Check Profile → Email digest is on. The digest goes out Monday
                  09:00 UTC. If the toggle is on and you still don't see it, check spam
                  for <code>hello@bulagolf.app</code> and whitelist that address.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="refund">
                <AccordionTrigger data-testid="help-faq-refund">I need to cancel Pro</AccordionTrigger>
                <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed">
                  Within 7 days of your most recent payment, tap <b>Cancel &amp; refund</b>
                  on the Pro section of your <Link to="/profile" className="text-[#E05D3A] font-bold">profile</Link> — full refund, no questions.
                  After 7 days the purchase is non-refundable but you keep Pro through
                  the rest of the paid period.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="contact">
                <AccordionTrigger data-testid="help-faq-contact">Something else? Talk to us</AccordionTrigger>
                <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed">
                  Drop us a line at <a href="mailto:support@bulagolf.fj" className="text-[#E05D3A] font-bold">support@bulagolf.fj</a>.
                  We're a small team in Suva — we usually reply within a business day.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Section>
        </div>
      </div>

      {/* Closing CTA */}
      <div className="card-default p-6 sm:p-8 text-center" data-testid="help-footer-cta">
        <h3 className="font-heading font-black text-2xl">Ready to play?</h3>
        <p className="text-sm text-[#0F2537]/70 mt-1">Bula! Tee it high, swing easy.</p>
        <div className="flex justify-center gap-2 mt-4 flex-wrap">
          <Link to="/rounds/new" data-testid="help-cta-new-round" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold hover:brightness-110 transition">
            <Golf size={14} weight="fill" /> Start a round
          </Link>
          <Link to="/courses" data-testid="help-cta-courses" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-white border border-[#E2E0D8] text-[#0F2537] text-sm font-bold hover:border-[#0F2537]/40 transition">
            <Flag size={14} weight="fill" /> Browse courses
          </Link>
        </div>
      </div>
    </div>
  );
}
